/**
 * Wrapper hooks for LaunchDarkly SDK that support both real and mock implementations
 * This allows E2E tests to mock feature flags via window.__MOCK_LAUNCHDARKLY_FLAGS__
 */

import {
  useFlags as useRealFlags,
  useLDClient as useRealLDClient
} from 'launchdarkly-react-client-sdk'
import { useContext } from 'react'
import { MockLDContext } from './FeatureFlagProvider'

/**
 * Hook that returns feature flags, using mock flags if available (E2E tests), otherwise real LaunchDarkly flags
 */
export const useFlags = (): Record<string, boolean> => {
  // Always call hooks unconditionally
  const mockContext = useContext(MockLDContext)
  const realFlags = useRealFlags()

  // Then conditionally return the appropriate value
  if (typeof window !== 'undefined' && window.__MOCK_LAUNCHDARKLY_FLAGS__ !== undefined) {
    if (mockContext) {
      return mockContext.flags
    }
    // Fallback to window flags if context not yet available
    return window.__MOCK_LAUNCHDARKLY_FLAGS__ || {}
  }
  // Use real LaunchDarkly flags
  return realFlags
}

/**
 * Hook that returns LaunchDarkly client, using mock client if available (E2E tests), otherwise real client
 */
type LDContextParam = Record<string, unknown>

export const useLDClient = ():
  | {
      identify: (context: LDContextParam) => void
      variation: (key: string, defaultValue: boolean) => boolean
      on: (event: string, callback: () => void) => void
      off: (event: string, callback: () => void) => void
    }
  | ReturnType<typeof useRealLDClient> => {
  // Always call hooks unconditionally
  const mockContext = useContext(MockLDContext)
  const realClient = useRealLDClient()

  // Then conditionally return the appropriate value
  if (typeof window !== 'undefined' && window.__MOCK_LAUNCHDARKLY_FLAGS__ !== undefined) {
    if (mockContext) {
      return mockContext.client
    }
    // Fallback to mock client if context not yet available
    const mockFlags = window.__MOCK_LAUNCHDARKLY_FLAGS__ || {}
    return {
      identify: (_context: LDContextParam) => {
        // No-op for tests
      },
      variation: (key: string, defaultValue: boolean): boolean => {
        // Return flag value from mock flags, or default if not found
        return mockFlags[key] ?? defaultValue
      },
      on: (_event: string, _callback: () => void) => {
        // No-op for tests - flag changes don't happen in E2E tests
      },
      off: (_event: string, _callback: () => void) => {
        // No-op for tests
      }
    }
  }
  // Use real LaunchDarkly client
  return realClient
}
